import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
	Box,
	Flex,
	Heading,
	Text,
	Button,
	Divider,
	Badge,
	Stack,
	useColorModeValue,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	Icon,
	VStack,
	HStack,
	Alert,
	AlertIcon,
	AlertTitle,
	AlertDescription,
	AlertDialog,
	AlertDialogBody,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogContent,
	AlertDialogOverlay,
} from '@chakra-ui/react';
import {
	FaCheckCircle,
	FaTimesCircle,
	FaCalendarAlt,
	FaArrowDown,
	FaExchangeAlt,
} from 'react-icons/fa';
import { useStripePrice } from '../../../hooks/useStripe';
import {
	collectVisiblePlanMatrixFeatureNames,
	isFeatureVisibleOnPlanTable,
	shouldShowPlanMatrixCheckIcon,
} from '../../../utils/featureUtils';
import {
	normalizePlanLabel,
	resolveCurrentPlanIndex,
} from './subscriptionPlanMatrixContext';
import { filterPlansForSubscriptionTable } from '../../../utils/planDisplayUtils';

export { normalizePlanLabel, resolveCurrentPlanIndex };

/** @deprecated Prefer resolveCurrentPlanIndex — only one plan should match. */
export const isMatchingCurrentPlan = (plan, currentPlanId, currentPlanName, allPlans, isYearly) => {
	if (!plan || !Array.isArray(allPlans)) return false;
	const idx = allPlans.indexOf(plan);
	if (idx < 0) return false;
	const currentIdx = resolveCurrentPlanIndex(
		allPlans,
		{ subscriptionPriceId: currentPlanId, planName: currentPlanName },
		isYearly,
	);
	return idx === currentIdx;
};

const getStripePriceObject = (priceData) => {
	if (!priceData) return null;
	if (
		priceData.data?.data &&
		typeof priceData.data.data === 'object' &&
		priceData.data.data.unit_amount !== undefined
	) {
		return priceData.data.data;
	}
	if (priceData.data?.unit_amount !== undefined) {
		return priceData.data;
	}
	if (priceData.unit_amount !== undefined) {
		return priceData;
	}
	return null;
};

const getStripeUnitAmount = (priceData) => {
	const priceObj = getStripePriceObject(priceData);
	const unitAmount = priceObj?.unit_amount;
	if (unitAmount === null || unitAmount === undefined || Number.isNaN(unitAmount)) {
		return NaN;
	}
	return Number(unitAmount);
};

const formatMoneyFromCents = (cents, currency = 'usd') => {
	const amount = cents / 100;
	const code = currency.toUpperCase();
	const symbol = code === 'USD' ? '$' : `${code} `;
	return `${symbol}${amount.toLocaleString(undefined, {
		minimumFractionDigits: code === 'USD' ? 0 : 2,
		maximumFractionDigits: code === 'USD' ? 0 : 2,
	})}`;
};

/** Annual savings vs paying the monthly price × 12 (both from Stripe unit_amount in cents). */
const calculateAnnualSavings = (monthlyPriceData, yearlyPriceData) => {
	const monthlyCents = getStripeUnitAmount(monthlyPriceData);
	const yearlyCents = getStripeUnitAmount(yearlyPriceData);
	if (!Number.isFinite(monthlyCents) || !Number.isFinite(yearlyCents) || monthlyCents <= 0) {
		return null;
	}

	const billedMonthlyForYearCents = monthlyCents * 12;
	const savingsCents = billedMonthlyForYearCents - yearlyCents;
	if (savingsCents <= 0) return null;

	const currency = getStripePriceObject(yearlyPriceData)?.currency || 'usd';

	return {
		savingsCents,
		formatted: formatMoneyFromCents(savingsCents, currency),
		percentOff: Math.round((savingsCents / billedMonthlyForYearCents) * 100),
		billedMonthlyForYearFormatted: formatMoneyFromCents(billedMonthlyForYearCents, currency),
	};
};

const parsePlanAmount = (value) => {
	if (value === null || value === undefined) return NaN;
	if (typeof value === 'number') {
		if (Number.isInteger(value) && value > 999) return value;
		return value * 100;
	}
	if (typeof value === 'string') {
		const normalized = value.replace(/[^0-9.-]/g, '');
		const parsed = parseFloat(normalized);
		if (!Number.isFinite(parsed)) return NaN;
		return parsed < 1000 ? parsed * 100 : parsed;
	}
	if (typeof value === 'object') {
		return parsePlanAmount(value.amount ?? value.price ?? value.unit_amount);
	}
	return NaN;
};

const getPlanFallbackAmount = (plan, isYearly) => {
	const intervalAmount = isYearly
		? (plan.amountYearly ??
			plan.yearlyAmount ??
			plan.priceYearly ??
			plan.yearlyPrice ??
			plan.amount ??
			plan.price)
		: (plan.amountMonthly ??
			plan.monthlyAmount ??
			plan.priceMonthly ??
			plan.monthlyPrice ??
			plan.amount ??
			plan.price);

	const parsed = parsePlanAmount(intervalAmount);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : NaN;
};

const resolveSwitchLabel = ({
	targetAmountCents,
	currentAmountCents,
	planIndex,
	currentPlanIndex,
}) => {
	if (Number.isFinite(targetAmountCents) && Number.isFinite(currentAmountCents)) {
		if (targetAmountCents > currentAmountCents) return 'Upgrade';
		if (targetAmountCents < currentAmountCents) return 'Downgrade';
		return 'Switch';
	}
	if (currentPlanIndex >= 0 && planIndex >= 0) {
		if (planIndex > currentPlanIndex) return 'Upgrade';
		if (planIndex < currentPlanIndex) return 'Downgrade';
	}
	return 'Switch';
};


const COLUMN_ACCENTS = ['teal', 'blue', 'purple', 'orange', 'cyan'];

/** Compact billing toggle for the sticky Features column header */
const BillingCycleMiniToggle = ({ isYearly, onChange }) => (
	<HStack
		spacing={0}
		borderRadius='full'
		overflow='hidden'
		border='1px solid'
		borderColor='gray.200'
		bg='gray.50'
		w='full'
		maxW='156px'
		position='relative'
		role='group'
		aria-label='Billing cycle'
		onClick={(e) => e.stopPropagation()}>
		<Box
			position='absolute'
			top='1px'
			bottom='1px'
			left={isYearly ? 'calc(50% - 1px)' : '1px'}
			w='calc(50% - 1px)'
			bg='teal.500'
			borderRadius='full'
			transition='left 0.2s ease'
			zIndex={0}
		/>
		{[
			{ yearly: false, label: 'Monthly' },
			{ yearly: true, label: 'Yearly' },
		].map(({ yearly, label }) => (
			<Box
				as='button'
				type='button'
				key={label}
				flex={1}
				py={1.5}
				px={1}
				fontSize='10px'
				fontWeight={isYearly === yearly ? '700' : '500'}
				color={isYearly === yearly ? 'white' : 'gray.600'}
				cursor='pointer'
				position='relative'
				zIndex={1}
				lineHeight='1.2'
				textAlign='center'
				onClick={() => onChange(yearly)}
				_hover={{ opacity: 0.92 }}>
				{label}
			</Box>
		))}
	</HStack>
);

const PlanSwitchButton = ({
	plan,
	currentPlan,
	isYearly,
	currentPlanPrice,
	planIndex,
	currentPlanIndex,
	onSwitch,
	firstTimeSubscribe = false,
	pricingMode = 'subscription',
}) => {
	const isLifetimePlan = pricingMode === 'lifetime';
	const targetPriceId = isLifetimePlan
		? plan.stripePriceIdOneTime
		: isYearly
			? plan.stripePriceIdYearly
			: plan.stripePriceIdMonthly;
	const currentPriceId = currentPlan
		? isYearly
			? currentPlan.stripePriceIdYearly
			: currentPlan.stripePriceIdMonthly
		: null;

	const { data: targetPriceData, isLoading: targetLoading } =
		useStripePrice(targetPriceId);
	const { data: currentPriceData, isLoading: currentLoading } =
		useStripePrice(currentPriceId);

	const targetAmountCents = getStripeUnitAmount(targetPriceData);
	const currentAmountCents =
		getStripeUnitAmount(currentPriceData) ??
		parsePlanAmount(currentPlanPrice) ??
		(currentPlan ? getPlanFallbackAmount(currentPlan, isYearly) : NaN);

	const label = !currentPlan
		? firstTimeSubscribe
			? isLifetimePlan
				? 'Buy lifetime'
				: 'Get started'
			: 'Subscribe'
		: resolveSwitchLabel({
				targetAmountCents,
				currentAmountCents,
				planIndex,
				currentPlanIndex,
			});

	const isLoading =
		(targetLoading || currentLoading) && !Number.isFinite(targetAmountCents);

	const colorScheme =
		label === 'Upgrade'
			? 'teal'
			: label === 'Downgrade'
				? 'orange'
				: isLifetimePlan
					? 'orange'
					: firstTimeSubscribe || !currentPlan
						? 'teal'
						: 'blue';

	return (
		<Button
			colorScheme={colorScheme}
			size={firstTimeSubscribe ? 'md' : 'sm'}
			w={firstTimeSubscribe ? 'full' : undefined}
			maxW={firstTimeSubscribe ? '200px' : undefined}
			borderRadius='lg'
			boxShadow={firstTimeSubscribe ? 'sm' : undefined}
			_hover={firstTimeSubscribe ? { transform: 'translateY(-1px)', boxShadow: 'md' } : undefined}
			onClick={() => onSwitch(plan)}
			isLoading={isLoading}>
			{label}
		</Button>
	);
};

const SubscriptionPlanMatrix = ({
	plans,
	currentPlanId,
	currentPlanName,
	subscriptionPriceId: subscriptionPriceIdProp,
	planMongoId,
	currentPlanPrice,
	onSelectPlan,
	onUpdatePlan,
	onCancelSubscription,
	dueToBeCanceledDate,
	subscriptionStatus,
	openCustomerPortal,
	showTitle = true,
	showPrice = true,
	isYearly = false,
	bgColor = 'white',
	stickyPlanHeader = false,
	stickyHeaderOffset = '80px',
	firstTimeSubscribe = false,
	showMoneyBackGuarantee = false,
	onBillingCycleChange,
	pricingMode = 'subscription',
}) => {
	const [showCancelConfirm, setShowCancelConfirm] = useState(false);
	const [isHeaderStuck, setIsHeaderStuck] = useState(false);
	const cancelRef = useRef();
	const stickySentinelRef = useRef(null);

	useEffect(() => {
		if (!stickyPlanHeader) {
			setIsHeaderStuck(false);
			return undefined;
		}

		const sentinel = stickySentinelRef.current;
		if (!sentinel) return undefined;

		const offsetPx = Number.parseInt(String(stickyHeaderOffset), 10) || 0;

		const findScrollRoot = (element) => {
			let node = element.parentElement;
			while (node && node !== document.body) {
				const { overflowY } = window.getComputedStyle(node);
				if (
					/(auto|scroll|overlay)/.test(overflowY) &&
					node.scrollHeight > node.clientHeight
				) {
					return node;
				}
				node = node.parentElement;
			}
			return null;
		};

		const root = findScrollRoot(sentinel);

		const observer = new IntersectionObserver(
			([entry]) => {
				setIsHeaderStuck(!entry.isIntersecting);
			},
			{
				root,
				rootMargin: `-${offsetPx}px 0px 0px 0px`,
				threshold: 0,
			},
		);

		observer.observe(sentinel);
		return () => observer.disconnect();
	}, [stickyPlanHeader, stickyHeaderOffset]);

	const compactStickyHeader = stickyPlanHeader && isHeaderStuck;
	const featureNameFontSize = firstTimeSubscribe ? 'xs' : 'sm';
	const featureValueFontSize = firstTimeSubscribe ? 'xs' : 'sm';
	const featureCellPy = firstTimeSubscribe ? 2 : 3;
	const featureCheckSize = firstTimeSubscribe ? 3 : 3.5;
	const featureCheckBox = firstTimeSubscribe ? 6 : 7;

	const handlePlanSwitch = (plan) => {
		if (typeof onUpdatePlan === 'function') {
			onUpdatePlan(plan);
			return;
		}
		if (typeof onSelectPlan === 'function') {
			onSelectPlan(plan);
		}
	};

	const handleSwitchPlan = (plan) => {
		if (subscriptionStatus === 'active' && typeof onUpdatePlan === 'function') {
			onUpdatePlan(plan);
			return;
		}
		handlePlanSwitch(plan);
	};

	// Row background colors (light/dark mode aware)
	const rowBgEven = useColorModeValue('gray.50', 'gray.700');
	const rowBgOdd = useColorModeValue('white', 'gray.800');
	const rowHover = useColorModeValue('gray.100', 'gray.600');
	// Per-column color palette (light/dark aware)
	const lightPalette = [
		'blue.50',
		'green.50',
		'purple.50',
		'orange.50',
		'teal.50',
	];
	const darkPalette = [
		'blue.800',
		'green.800',
		'purple.800',
		'orange.800',
		'teal.800',
	];
	const columnPalette = useColorModeValue(lightPalette, darkPalette);
	const stickyHeaderBg = useColorModeValue('white', 'gray.800');
	const stickyHeaderShadow = useColorModeValue(
		'0 2px 8px rgba(0,0,0,0.08)',
		'0 2px 8px rgba(0,0,0,0.35)',
	);
	const currentPlanColBg = useColorModeValue('teal.50', 'teal.900');
	const currentPlanBorder = useColorModeValue('teal.400', 'teal.300');

	const stickyThSx = (overrides = {}) =>
		stickyPlanHeader
			? {
					position: 'sticky',
					top: stickyHeaderOffset,
					zIndex: 10,
					boxShadow: stickyHeaderShadow,
					...overrides,
				}
			: overrides;

	const stickyFeatureHeaderSx = stickyPlanHeader
		? stickyThSx({
				left: 0,
				zIndex: 12,
				overflow: 'visible',
				boxShadow: `${stickyHeaderShadow}, 2px 0 4px rgba(0,0,0,0.06)`,
			})
		: {};

	const stickyFeatureColSx = stickyPlanHeader
		? {
				position: 'sticky',
				left: 0,
				zIndex: 4,
				boxShadow: '4px 0 8px -4px rgba(0,0,0,0.08)',
			}
		: {
				position: 'sticky',
				left: 0,
				zIndex: 3,
			};

	// Price formatting function
	const formatPrice = (priceData) => {
		// Handle different data structures that might come from React Query
		let priceObj = null;

		if (!priceData) return null;

		// Check for the nested structure: priceData.data.data (API response structure)
		if (
			priceData.data &&
			priceData.data.data &&
			typeof priceData.data.data === 'object' &&
			priceData.data.data.unit_amount !== undefined
		) {
			priceObj = priceData.data.data;
		}
		// Check if priceData.data is the direct response
		else if (
			priceData.data &&
			typeof priceData.data === 'object' &&
			priceData.data.unit_amount !== undefined
		) {
			priceObj = priceData.data;
		}
		// Check if priceData is the direct price object
		else if (priceData.unit_amount !== undefined) {
			priceObj = priceData;
		} else {
			return null;
		}

		const unitAmount = priceObj.unit_amount;

		// Validate that unit_amount is a valid number
		if (unitAmount === null || unitAmount === undefined || isNaN(unitAmount)) {
			return null;
		}

		const amount = unitAmount / 100; // Convert from cents
		const currency = priceObj.currency?.toUpperCase() || 'USD';
		const interval = priceObj.recurring?.interval || 'month';

		return {
			amount: amount.toLocaleString(),
			currency,
			interval,
			formatted: `$${amount.toLocaleString()} ${currency} / ${interval}`,
		};
	};

	const PlanHeader = ({
		plan,
		colColor,
		pIdx,
		showPrice,
		isYearly,
		isCurrentPlan,
		compact,
	}) => {
		const isLifetimePlan = pricingMode === 'lifetime';
		const accent = isLifetimePlan
			? 'orange'
			: COLUMN_ACCENTS[pIdx % COLUMN_ACCENTS.length];
		const { data: monthlyPrice, isLoading: monthlyLoading } = useStripePrice(
			!isLifetimePlan ? plan.stripePriceIdMonthly : null,
		);
		const { data: yearlyPrice, isLoading: yearlyLoading } = useStripePrice(
			!isLifetimePlan ? plan.stripePriceIdYearly : null,
		);
		const { data: oneTimePrice, isLoading: oneTimeLoading } = useStripePrice(
			isLifetimePlan ? plan.stripePriceIdOneTime : null,
		);

		const monthlyPriceInfo = formatPrice(monthlyPrice);
		const yearlyPriceInfo = formatPrice(yearlyPrice);
		const oneTimePriceInfo = formatPrice(oneTimePrice);

		const displayPrice = isLifetimePlan
			? oneTimePriceInfo
			: isYearly
				? yearlyPriceInfo
				: monthlyPriceInfo;
		const isLoading = isLifetimePlan
			? oneTimeLoading
			: isYearly
				? yearlyLoading || monthlyLoading
				: monthlyLoading;
		const annualSavings =
			!isLifetimePlan && isYearly && !monthlyLoading && !yearlyLoading
				? calculateAnnualSavings(monthlyPrice, yearlyPrice)
				: null;

		return (
			<Th
				textAlign='center'
				bg={isCurrentPlan ? currentPlanColBg : colColor}
				borderLeftWidth={pIdx === 0 ? '0' : '1px'}
				borderLeftColor='gray.100'
				borderBottomWidth={compact ? '2px' : '3px'}
				borderBottomColor={isCurrentPlan ? 'teal.500' : `${accent}.400`}
				py={compact ? 2 : 5}
				minW={{ base: '160px', md: '180px' }}
				transition='padding 0.2s ease, box-shadow 0.2s ease'
				sx={stickyThSx({
					bg: isCurrentPlan ? currentPlanColBg : colColor,
				})}>
				<Flex direction='column' align='center' gap={compact ? 1 : 2}>
					{isCurrentPlan && (
						<Badge
							colorScheme='teal'
							variant='solid'
							fontSize={compact ? '0.55em' : '0.65em'}
							px={2}
							borderRadius='full'>
							Your plan
						</Badge>
					)}
					{(plan.popular || plan.isPopular) && !isCurrentPlan && (
						<Badge
							colorScheme='purple'
							fontSize={compact ? '0.55em' : '0.65em'}
							px={2}
							borderRadius='full'>
							Most popular
						</Badge>
					)}
					<Heading
						as='h4'
						size={compact ? 'xs' : 'sm'}
						fontWeight='800'
						color='gray.800'
						m={0}
						lineHeight='1.2'
						noOfLines={2}>
						{plan.name || plan.planName || 'Unnamed Plan'}
					</Heading>
					{showPrice && (
						<VStack spacing={0}>
							{isLoading ? (
								<Text fontSize='xs' color='gray.500' fontWeight={600}>
									Loading...
								</Text>
							) : displayPrice ? (
								<>
									<HStack align='baseline' spacing={1}>
										<Text
											fontWeight='800'
											fontSize={compact ? 'lg' : '2xl'}
											color={`${accent}.600`}
											m={0}
											lineHeight='1.1'
											transition='font-size 0.2s ease'>
											${displayPrice.amount}
										</Text>
									</HStack>
									<Text
										fontSize={compact ? '10px' : 'xs'}
										color='gray.500'
										m={0}
										textTransform='capitalize'>
										{isLifetimePlan ? 'one-time payment' : `per ${displayPrice.interval}`}
									</Text>
									{annualSavings && !compact && (
										<VStack spacing={0.5} mt={1}>
											<Badge
												colorScheme='green'
												variant='solid'
												borderRadius='full'
												px={2}
												py={0.5}
												fontSize='0.65em'
												fontWeight='700'>
												Save {annualSavings.formatted}
											</Badge>
											{annualSavings.percentOff > 0 && (
												<Text fontSize='10px' color='green.600' m={0} lineHeight='1.3'>
													vs {annualSavings.billedMonthlyForYearFormatted}/yr at monthly rate
												</Text>
											)}
										</VStack>
									)}
									{annualSavings && compact && (
										<Badge
											colorScheme='green'
											variant='solid'
											borderRadius='full'
											px={1.5}
											py={0}
											mt={0.5}
											fontSize='9px'
											fontWeight='700'>
											Save {annualSavings.formatted}
										</Badge>
									)}
								</>
							) : (
								<Button variant='outline' colorScheme='gray' size='sm' borderRadius='lg'>
									Contact sales
								</Button>
							)}
						</VStack>
					)}
				</Flex>
			</Th>
		);
	};

	const visiblePlans = useMemo(
		() =>
			filterPlansForSubscriptionTable(plans, {
				subscriptionPriceId: subscriptionPriceIdProp,
				planMongoId,
				planId: planMongoId,
				currentPlanName,
				isYearly,
			}),
		[plans, subscriptionPriceIdProp, planMongoId, currentPlanName, isYearly],
	);

	// Sort plans in the desired order: Starter, Freelancer, Agency, Bespoke (or AppSumo tier)
	const plannOrder = ['Starter', 'Freelancer', 'Agency', 'Bespoke'];
	const sortedPlans = Array.isArray(visiblePlans)
		? [...visiblePlans].sort((a, b) => {
				if (pricingMode === 'lifetime') {
					return (a.appsumoTier || 99) - (b.appsumoTier || 99);
				}

				const nameA = (a.name || a.planName || '').toLowerCase();
				const nameB = (b.name || b.planName || '').toLowerCase();
				const indexA = plannOrder.findIndex((order) =>
					nameA.includes(order.toLowerCase()),
				);
				const indexB = plannOrder.findIndex((order) =>
					nameB.includes(order.toLowerCase()),
				);
				return (
					(indexA === -1 ? plannOrder.length : indexA) -
					(indexB === -1 ? plannOrder.length : indexB)
				);
			})
		: [];

	const allFeatures = collectVisiblePlanMatrixFeatureNames(sortedPlans);
	const subscriptionPriceId = subscriptionPriceIdProp ?? currentPlanId ?? null;
	const currentPlanIndex = resolveCurrentPlanIndex(
		sortedPlans,
		{
			subscriptionPriceId,
			planId: planMongoId,
			planName: currentPlanName,
		},
		isYearly,
	);
	const isPlanCurrent = (pIdx) => pIdx === currentPlanIndex;
	const currentPlan = currentPlanIndex >= 0 ? sortedPlans[currentPlanIndex] : null;

	return (
		<Box
			w='100%'
			overflowX={stickyPlanHeader ? 'visible' : 'auto'}
			overflowY='visible'
			bgColor={bgColor}
			borderRadius='xl'>
			{/* Cancel Subscription Confirmation Dialog */}
			<AlertDialog
				isOpen={showCancelConfirm}
				leastDestructiveRef={cancelRef}
				onClose={() => setShowCancelConfirm(false)}
				isCentered
				size='lg'>
				<AlertDialogOverlay />
				<AlertDialogContent>
					<AlertDialogHeader
						fontSize='lg'
						fontWeight='bold'
						borderBottom='1px solid'
						borderColor='gray.100'
						pb={3}>
						Cancel Subscription
					</AlertDialogHeader>
					<AlertDialogBody pt={4}>
						<VStack spacing={4} align='stretch'>
							{/* Suggest alternative: downgrade or upgrade based on available plans */}
							{(() => {
								const allPlans = Array.isArray(sortedPlans) ? sortedPlans : [];

								const parseAmount = (value) => {
									if (value === null || value === undefined) return NaN;
									if (typeof value === 'number') {
										if (Number.isInteger(value) && value > 999) return value / 100;
										return value;
									}
									if (typeof value === 'string') {
										const normalized = value.replace(/[^0-9.-]/g, '');
										const parsed = parseFloat(normalized);
										return Number.isFinite(parsed) ? parsed : NaN;
									}
									if (typeof value === 'object') {
										return parseAmount(value.amount ?? value.price ?? value.unit_amount);
									}
									return NaN;
								};

								const getPlanAmount = (plan) => {
									const intervalAmount = isYearly
										? (plan.amountYearly ??
											plan.yearlyAmount ??
											plan.priceYearly ??
											plan.yearlyPrice ??
											plan.amount ??
											plan.price)
										: (plan.amountMonthly ??
											plan.monthlyAmount ??
											plan.priceMonthly ??
											plan.monthlyPrice ??
											plan.amount ??
											plan.price);

									const parsed = parseAmount(intervalAmount);
									return Number.isFinite(parsed) && parsed > 0 ? parsed : NaN;
								};

								const isCurrentPlan = (p) =>
									p.stripePriceId === currentPlanId ||
									p.stripePriceIdMonthly === currentPlanId ||
									p.stripePriceIdYearly === currentPlanId ||
									p.id === currentPlanId ||
									p._id === currentPlanId;

								const currentAmount = parseAmount(currentPlanPrice);
								const currentPlanIndex = allPlans.findIndex((p) => isCurrentPlan(p));

								const cheaperPlansByAmount = Number.isFinite(currentAmount)
									? allPlans.filter((p) => {
											if (isCurrentPlan(p)) return false;
											const planAmount = getPlanAmount(p);
											return Number.isFinite(planAmount) && planAmount < currentAmount;
										})
									: [];

								const expensivePlansByAmount = Number.isFinite(currentAmount)
									? allPlans.filter((p) => {
											if (isCurrentPlan(p)) return false;
											const planAmount = getPlanAmount(p);
											return Number.isFinite(planAmount) && planAmount > currentAmount;
										})
									: [];

								const cheaperPlansByOrder =
									currentPlanIndex > 0 ? allPlans.slice(0, currentPlanIndex) : [];
								const expensivePlansByOrder =
									currentPlanIndex >= 0 ? allPlans.slice(currentPlanIndex + 1) : [];

								const cheaperPlans =
									cheaperPlansByAmount.length > 0
										? cheaperPlansByAmount
										: cheaperPlansByOrder;
								const expensivePlans =
									expensivePlansByAmount.length > 0
										? expensivePlansByAmount
										: expensivePlansByOrder;

								const hasDowngradeOption = cheaperPlans.length > 0;
								const hasUpgradeOption = expensivePlans.length > 0;

								if (hasDowngradeOption) {
									// User is on a higher plan - offer downgrade
									const lowestCheaperPlan = cheaperPlans.reduce((lowest, plan) => {
										if (!lowest) return plan;
										const planAmount = getPlanAmount(plan);
										const lowestAmount = getPlanAmount(lowest);
										if (!Number.isFinite(planAmount)) return lowest;
										if (!Number.isFinite(lowestAmount)) return plan;
										return planAmount < lowestAmount ? plan : lowest;
									}, null);

									return (
										<Box
											p={3}
											bg='blue.50'
											borderRadius='lg'
											border='1px solid'
											borderColor='blue.100'>
											<HStack mb={1}>
												<Icon as={FaArrowDown} color='blue.500' boxSize={3} />
												<Text fontSize='sm' fontWeight='700' color='blue.700'>
													Reduce costs with a lower plan
												</Text>
											</HStack>
											<Text fontSize='xs' color='blue.600' mb={3}>
												Keep your account and data active while paying less. You can always
												upgrade later.
											</Text>
											<VStack spacing={2} align='stretch'>
												<Button
													size='sm'
													colorScheme='blue'
													variant='solid'
													leftIcon={<Icon as={FaArrowDown} />}
													onClick={() => {
														setShowCancelConfirm(false);
														handlePlanSwitch(lowestCheaperPlan);
													}}>
													Switch to {lowestCheaperPlan.planName || lowestCheaperPlan.name}
												</Button>
											</VStack>
										</Box>
									);
								} else if (hasUpgradeOption) {
									// User is on the lowest plan - offer upgrade
									return (
										<Box
											p={3}
											bg='orange.50'
											borderRadius='lg'
											border='1px solid'
											borderColor='orange.100'>
											<HStack mb={1}>
												<Icon as={FaExchangeAlt} color='orange.500' boxSize={3} />
												<Text fontSize='sm' fontWeight='700' color='orange.700'>
													Need more features?
												</Text>
											</HStack>
											<Text fontSize='xs' color='orange.600' mb={3}>
												Your current features might not be enough. Explore our higher plans
												for advanced capabilities.
											</Text>
										</Box>
									);
								}

								return null;
							})()}

							{/* What happens when cancelled */}
							<Alert status='warning' borderRadius='md'>
								<AlertIcon />
								<Box>
									<AlertTitle fontSize='sm'>
										Your access will end at the next billing date
									</AlertTitle>
									<AlertDescription fontSize='xs'>
										Your plan will remain active until the end of your current billing
										period. After that, you'll lose access to all paid features.
									</AlertDescription>
								</Box>
							</Alert>
						</VStack>
					</AlertDialogBody>
					<AlertDialogFooter
						gap={2}
						borderTop='1px solid'
						borderColor='gray.100'
						pt={4}>
						<Button
							colorScheme='red'
							onClick={() => {
								setShowCancelConfirm(false);
								onCancelSubscription(currentPlanId);
							}}
							loadingText='Cancelling…'>
							Yes, Cancel
						</Button>
						<Button
							ref={cancelRef}
							variant='ghost'
							onClick={() => setShowCancelConfirm(false)}>
							Keep Subscription
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{sortedPlans && Array.isArray(sortedPlans) && sortedPlans.length > 0 ? (
				<>
					{stickyPlanHeader && (
						<Box ref={stickySentinelRef} h='1px' w='full' aria-hidden pointerEvents='none' />
					)}
					<Table
					variant='simple'
					size='md'
					minW={sortedPlans.length > 2 ? '720px' : '100%'}
					sx={{
						borderCollapse: 'separate',
						borderSpacing: 0,
						...(stickyPlanHeader && {
							'& thead': { overflow: 'visible' },
							'& thead th': {
								position: 'sticky',
								top: stickyHeaderOffset,
								overflow: 'visible',
							},
						}),
					}}>
					<Thead>
						<Tr>
							<Th
								bg={stickyPlanHeader ? stickyHeaderBg : rowBgOdd}
								color='gray.600'
								fontSize='xs'
								verticalAlign='top'
								minW='200px'
								py={compactStickyHeader ? 2 : 4}
								transition='padding 0.2s ease'
								overflow='visible'
								sx={stickyFeatureHeaderSx}>
								<VStack align='flex-start' spacing={compactStickyHeader ? 1.5 : 2} w='full'>
									<Text
										mt={compactStickyHeader ? 3 : 5}
										mr={0}
										ml={0}
										mb={compactStickyHeader ? 3 : 0}
										fontSize={compactStickyHeader ? '14px' : 'md'}
										fontWeight={compactStickyHeader ? '600' : '700'}
										textTransform='uppercase'
										letterSpacing='wider'
										color='gray.600'>
										Features
									</Text>
									{stickyPlanHeader && onBillingCycleChange && pricingMode !== 'lifetime' && (
										<Box
											w='full'
											opacity={compactStickyHeader ? 1 : 0}
											maxH={compactStickyHeader ? '40px' : '0'}
											overflow='hidden'
											transition='opacity 0.2s ease, max-height 0.2s ease'
											pointerEvents={compactStickyHeader ? 'auto' : 'none'}
											aria-hidden={!compactStickyHeader}>
											<BillingCycleMiniToggle
												isYearly={isYearly}
												onChange={onBillingCycleChange}
											/>
										</Box>
									)}
								</VStack>
							</Th>
							{sortedPlans.map((plan, pIdx) => {
								const colColor = columnPalette[pIdx % columnPalette.length];
								const isCurrentPlan = isPlanCurrent(pIdx);
								return (
									<PlanHeader
										key={plan.id || plan._id || pIdx}
										plan={plan}
										colColor={colColor}
										pIdx={pIdx}
										showPrice={showPrice}
										isYearly={isYearly}
										isCurrentPlan={isCurrentPlan}
										compact={compactStickyHeader}
									/>
								);
							})}
						</Tr>
					</Thead>
					<Tbody>
						{allFeatures.map((featureName, idx) => (
							<Tr
								key={featureName}
								bg={idx % 2 === 0 ? rowBgEven : rowBgOdd}
								_hover={{ bg: rowHover }}
								transition='background-color 0.15s ease'>
								<Td
									fontWeight='600'
									color='gray.700'
									fontSize={featureNameFontSize}
									py={featureCellPy}
									bg={idx % 2 === 0 ? rowBgEven : rowBgOdd}
									borderRight='1px solid'
									borderColor='gray.100'
									sx={stickyFeatureColSx}>
									{featureName}
								</Td>
								{sortedPlans.map((plan, pIdx) => {
									const feature = plan.features?.find(
										(f) =>
											f?.featureName === featureName &&
											isFeatureVisibleOnPlanTable(f),
									);
									const colColor = columnPalette[pIdx % columnPalette.length];
									const isCurrentPlan = isPlanCurrent(pIdx);
									return (
										<Td
											key={plan.id || plan._id || pIdx}
											textAlign='center'
											bg={isCurrentPlan ? currentPlanColBg : undefined}
											borderLeftWidth='1px'
											borderLeftStyle='solid'
											borderLeftColor='gray.100'
											py={featureCellPy}>
											{feature ? (
												(() => {
													const planLimit = feature.planLimits?.find(
														(pl) =>
															String(pl.plan?._id || pl.plan) ===
															String(plan._id || plan.id),
													);
													const displayText =
														planLimit?.displayLimit ||
														planLimit?.readableLimit ||
														planLimit?.limit;

													return shouldShowPlanMatrixCheckIcon(displayText) ? (
														<Flex justify='center'>
															<Flex
																align='center'
																justify='center'
																w={featureCheckBox}
																h={featureCheckBox}
																borderRadius='full'
																bg='green.50'>
																<Icon
																	as={FaCheckCircle}
																	color='green.500'
																	boxSize={featureCheckSize}
																/>
															</Flex>
														</Flex>
													) : (
														<Text
															fontWeight='600'
															fontSize={featureValueFontSize}
															color='gray.700'
															m={0}
															lineHeight='1.35'>
															{displayText}
														</Text>
													);
												})()
											) : (
												<Flex justify='center'>
													<Flex
														align='center'
														justify='center'
														w={featureCheckBox}
														h={featureCheckBox}
														borderRadius='full'
														bg='red.50'>
														<Icon
															as={FaTimesCircle}
															color='red.400'
															boxSize={featureCheckSize}
														/>
													</Flex>
												</Flex>
											)}
										</Td>
									);
								})}
							</Tr>
						))}
						{/* Action Buttons Row */}
						<Tr bg={allFeatures.length % 2 === 0 ? rowBgEven : rowBgOdd}>
							<Td
								bg={allFeatures.length % 2 === 0 ? rowBgEven : rowBgOdd}
								borderRight='1px solid'
								borderColor='gray.100'
								sx={stickyFeatureColSx}
							/>
							{sortedPlans.map((plan, pIdx) => {
								const isActivePlan = isPlanCurrent(pIdx);
								return (
									<Td
										key={plan.id || plan._id || pIdx}
										textAlign='center'
										bg={isActivePlan ? currentPlanColBg : undefined}
										borderLeftWidth='1px'
										borderLeftStyle='solid'
										borderLeftColor='gray.100'
										py={5}
										verticalAlign='middle'>
										{isActivePlan ? (
											<VStack spacing={2}>
												<Button
													colorScheme='teal'
													size='sm'
													isDisabled
													cursor='not-allowed'
													_hover={{}}>
													Current plan
												</Button>
												{subscriptionStatus === 'active' &&
													typeof openCustomerPortal === 'function' && (
														<>
															<Button
																colorScheme='teal'
																size='sm'
																variant='outline'
																onClick={openCustomerPortal}>
																Manage Plan
															</Button>
															<Text fontSize='xs' color='gray.500' textAlign='center'>
																Secure portal
															</Text>
														</>
													)}
												{subscriptionStatus === 'active' && !dueToBeCanceledDate && (
													<Button
														variant='outline'
														colorScheme='red'
														size='sm'
														onClick={() => setShowCancelConfirm(true)}>
														Cancel Subscription
													</Button>
												)}
											</VStack>
										) : plan.planUniqueKey === 'bespoke_plan' ? (
											<VStack spacing={1}>
												<Button
													as='a'
													href='mailto:support@opspilotcrm.com'
													colorScheme='purple'
													size='sm'
													variant='outline'>
													Contact Us
												</Button>
												<Text fontSize='xs' color='gray.500' textAlign='center'>
													Custom pricing available
												</Text>
											</VStack>
										) : plan.stripePriceIdMonthly ||
										  plan.stripePriceIdYearly ||
										  plan.stripePriceIdOneTime ? (
											<VStack spacing={2} px={2}>
												<PlanSwitchButton
													plan={plan}
													currentPlan={currentPlan}
													isYearly={isYearly}
													currentPlanPrice={currentPlanPrice}
													planIndex={pIdx}
													currentPlanIndex={currentPlanIndex}
													onSwitch={handleSwitchPlan}
													firstTimeSubscribe={firstTimeSubscribe}
													pricingMode={pricingMode}
												/>
												{showMoneyBackGuarantee && pricingMode !== 'lifetime' && (
													<Text fontSize='xs' color='gray.500' textAlign='center' m={0}>
														30-day money-back guarantee
													</Text>
												)}
												{firstTimeSubscribe && pricingMode === 'lifetime' && (
													<Text fontSize='xs' color='gray.500' textAlign='center' m={0}>
														One-time payment · lifetime access
													</Text>
												)}
											</VStack>
										) : null}
									</Td>
								);
							})}
						</Tr>
					</Tbody>
				</Table>
				</>
			) : (
				<Box textAlign='center' py={8}>
					<Text color='gray.500'>Loading plans...</Text>
				</Box>
			)}
		</Box>
	);
};

export default SubscriptionPlanMatrix;
